/** Chart and drawer rules for Market → Funding. Display only; nothing is rewritten. */

export const AWARDING_EMPTY_COPY =
  'Awarding organisations are not funded through membership grant schemes. One UK programme gates on recognition and has no open round.';

export const FUNDING_STATUS = {
  OPEN: { label: 'Open', className: 'bg-[#DBEAFE] text-[#1E40AF]' },
  PREANNOUNCED: { label: 'Pre-announced', className: 'bg-[#FEF3C7] text-[#92400E]' },
  ACTIVE_PROGRAMME: { label: 'Active programme', className: 'bg-[#DBEAFE] text-[#1E40AF]' },
  // A round whose deadline has passed and is being assessed. Without its own entry it fell through
  // to the generic fallback and rendered as the raw enum, "closed evaluation".
  CLOSED_EVALUATION: { label: 'Closed — under review', className: 'bg-[#FEE2E2] text-[#991B1B]' },
  RESULTS_PUBLISHED: { label: 'Results published', className: 'bg-[#FEF3C7] text-[#92400E]' },
  CLOSED_ARCHIVED: { label: 'Closed', className: 'bg-[#FEE2E2] text-[#991B1B]' },
};

export function fundingStatusMeta(status) {
  const key = String(status || '').trim().toUpperCase();
  return FUNDING_STATUS[key] || {
    label: key ? key.replace(/_/g, ' ').toLowerCase() : 'Unknown',
    className: 'bg-gray-100 text-[#6B7280]',
  };
}

export function isOpenFundingCall(call) {
  return String(call?.status || '').trim().toUpperCase() === 'OPEN';
}

/**
 * Does this programme NAME one of the tenant's lens categories, rather than merely sharing a theme?
 * The API decides this, not the UI: `callReachesLens` returns 'direct' when the call's own
 * `categories` name a lens category and 'theme' when only the themes overlap, and it sends both
 * `reaches_this_lens` (the boolean) and `reach` (the word). Read the boolean first and fall back to
 * the word, so neither field alone is load-bearing.
 */
export function isCategoryRelevantCall(call) {
  if (typeof call?.reaches_this_lens === 'boolean') return call.reaches_this_lens;
  return String(call?.reach || '').trim().toLowerCase() === 'direct';
}

/** A deadline is a firm date. Only an open round has one. */
export function fundingDeadline(call) {
  if (!isOpenFundingCall(call)) return null;
  const value = call?.deadline;
  if (value == null || String(value).trim() === '') return null;
  return String(value);
}

/**
 * What the deadline column says when it has no date to show. A bare '—' read the same for a standing
 * programme nobody applies to, a round whose date is not out yet, and a round that has already closed
 * — Tiago, 2026-11-03: Collaborative Enhancement "doesn't have dates, so it's a bit ambiguous".
 * Returns a translation key plus, for a closed round with a known deadline, the date it closed.
 */
export function fundingNoDeadlineNote(call) {
  const key = String(call?.status || '').trim().toUpperCase();
  if (key === 'ACTIVE_PROGRAMME') return { key: 'mktNoFixedDeadline', sub: 'mktStandingProgramme' };
  if (key === 'PREANNOUNCED') return { key: 'mktDateNotPublished' };
  if (key === 'CLOSED_EVALUATION' || key === 'RESULTS_PUBLISHED' || key === 'CLOSED_ARCHIVED') {
    // `closed_on` is the API's name for the date a closed round shut; `deadline` is only ever sent
    // for an open round, so it is a fallback for older payloads.
    const raw = call?.closed_on ?? call?.deadline;
    const d = raw != null && String(raw).trim() !== '' ? String(raw) : null;
    return d ? { key: 'mktClosedOn', date: d } : { key: 'mktWindowClosed' };
  }
  if (key === 'OPEN') return { key: 'mktNoDeadlinePublished' };
  return null;
}

export function urgencyColor(daysRemaining) {
  const n = Number(daysRemaining);
  if (!Number.isFinite(n)) return '#6B7280';
  if (n < 21) return '#DC2626';
  if (n <= 60) return '#D97706';
  return '#6B7280';
}

export function isZeroReachCall(call) {
  return Number(call?.eligible_accounts) === 0;
}

function firstText(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = typeof value === 'string' ? value.trim() : value;
    if (text === '') continue;
    return value;
  }
  return null;
}

function fundingSources(record) {
  if (!record || typeof record !== 'object') return [];
  const out = [];
  for (const key of ['funding', 'external_funding', 'funding_block']) {
    const value = record[key];
    if (Array.isArray(value)) out.push(...value.filter((item) => item && typeof item === 'object'));
    else if (value && typeof value === 'object') out.push(value);
  }
  if (Array.isArray(record.funding_calls)) {
    out.push(...record.funding_calls.filter((item) => item && typeof item === 'object'));
  }
  if (Array.isArray(record.programmes)) {
    out.push(...record.programmes.filter((item) => item && typeof item === 'object'));
  }
  return out;
}

export function normalizeFundingBlock(source) {
  if (!source || typeof source !== 'object') return null;
  const programme = firstText(source.programme_name, source.programme, source.name);
  const amount = firstText(source.amount_text, source.amount);
  const basis = firstText(source.eligibility_basis, source.basis);
  const verified = firstText(source.verified_at, source.verified_date, source.verified);
  const deadline = fundingDeadline(source);
  const eligibleSpend = source.eligible_spend != null ? String(source.eligible_spend) : null;
  const sourceUrl = firstText(source.source_url);
  if (!programme && !amount && !basis && !verified && !deadline && !eligibleSpend) return null;
  return {
    programme: programme ? String(programme) : null,
    amount: amount != null ? String(amount) : null,
    deadline,
    basis: basis != null ? String(basis) : null,
    verified: verified != null ? String(verified) : null,
    status: source.status || null,
    eligible_spend: eligibleSpend,
    source_url: sourceUrl ? String(sourceUrl) : null,
  };
}

/** Render the eligibility ceiling as the backend wrote it. Do not parse or sum currencies. */
export function displayFundingEligibility(value) {
  if (value == null) return null;
  const text = String(value);
  if (text.trim() === '') return null;
  return text;
}

/** Drawer block. Null when the account has no funding evidence — no empty state. */
export function fundingBlocksFromRecords(...records) {
  const seen = new Set();
  const blocks = [];
  for (const record of records) {
    for (const source of fundingSources(record)) {
      const block = normalizeFundingBlock(source);
      if (!block) continue;
      const key = [block.programme, block.amount, block.basis, block.verified].join('|');
      if (seen.has(key)) continue;
      seen.add(key);
      blocks.push(block);
    }
  }
  return blocks;
}

export function awardingEmptyCopy(tenantId, payload) {
  const id = String(tenantId || '').toLowerCase();
  if (!id.startsWith('surpass')) return null;
  const narrative = Array.isArray(payload?.narrative) ? payload.narrative.join('\n') : '';
  if (narrative.includes('Awarding organisations are not funded')) return null;
  const calls = Array.isArray(payload?.calls) ? payload.calls : [];
  if (calls.some((call) => isOpenFundingCall(call))) return null;
  return AWARDING_EMPTY_COPY;
}

function nestedCall(row) {
  return row?.call
    || row?.funding
    || row?.funding_block
    || row?.programmes?.[0]
    || row?.funding_calls?.[0]
    || row?.calls?.[0]
    || null;
}

/** Context posted with How to pitch. Eligible spend is verbatim. Non-open rounds carry no deadline. */
export function fundingPitchContext(row, call) {
  const nested = nestedCall(row);
  const src = { ...(call || {}), ...(nested || {}), ...(row || {}) };
  const status = String(src.status || call?.status || '').trim().toUpperCase();
  const open = status === 'OPEN';
  return {
    surface: 'market_funding',
    programme_name: firstText(src.programme_name, src.programme, call?.programme_name) || null,
    funder: firstText(src.funder, call?.funder) || null,
    amount_text: firstText(src.amount_text, src.amount, call?.amount_text) || null,
    deadline: open ? (firstText(src.deadline, call?.deadline) || null) : null,
    eligibility_basis: firstText(src.eligibility_basis, src.basis) || null,
    eligible_spend: src.eligible_spend != null ? String(src.eligible_spend) : null,
    applicant_type: firstText(src.applicant_type) || null,
    funder_objective: src.funder_objective != null ? String(src.funder_objective) : null,
    incumbent_vendor: firstText(src.incumbent_vendor) || null,
    contract_end_date: firstText(src.contract_end_date) || null,
    category: firstText(src.category) || null,
    market_score: src.market_score ?? row?.market_score ?? null,
    structural_score: src.structural_score ?? row?.structural_score ?? null,
  };
}

export function fundingPitchQuestion(programmeName, accountName) {
  const programme = programmeName || 'this programme';
  const account = accountName || 'this account';
  return `How should I pitch ${programme} to ${account}?`;
}

export const FUNDING_ACCOUNT_SORT_DEFAULTS = {
  account: 'asc',
  eligibility: 'asc',
  country: 'asc',
  standing: 'asc',
  stage: 'asc',
  fit: 'desc',
  in_market: 'desc',
};

export const FUNDING_STAGE = {
  open_round: { label: 'Open round', className: 'bg-[#DBEAFE] text-[#1E40AF]' },
  prior_award: { label: 'Already funded', className: 'bg-[#DCFCE7] text-[#166534]' },
  both: { label: 'Open + funded', className: 'bg-[#DCFCE7] text-[#166534]' },
};

export function fundingStageMeta(basis) {
  return FUNDING_STAGE[basis] || null;
}

/** Same funder as the open call. A grant from another body does not qualify. */
export function showWonBeforeBadge(row) {
  return row?.prior_funding_same_funder === true;
}

export function priorFundingEntries(...records) {
  const seen = new Set();
  const out = [];
  for (const record of records) {
    const list = record?.prior_funding;
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      const quote = item.evidence_quote != null ? String(item.evidence_quote) : '';
      if (!quote.trim() && !item.funder && !item.programme_name) continue;
      const key = [item.funder, item.programme_name, quote, item.document_date, item.source_url].join('|');
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        funder: item.funder ? String(item.funder) : null,
        programme_name: item.programme_name ? String(item.programme_name) : null,
        status: item.status || null,
        evidence_quote: quote,
        document_date: item.document_date || null,
        source_url: item.source_url ? String(item.source_url) : null,
        category_relevance: humaniseCategoryRelevance(item.category_relevance),
      });
    }
  }
  return out;
}

export function priorFundingYear(documentDate) {
  if (documentDate == null || String(documentDate).trim() === '') return null;
  const match = String(documentDate).match(/^(\d{4})/);
  return match ? match[1] : null;
}

export function humaniseCategoryRelevance(value) {
  if (value == null || String(value).trim() === '') return null;
  const text = String(value)
    .split(',')
    .map((part) => part.trim().replace(/_/g, ' '))
    .filter(Boolean)
    .join(', ');
  return text || null;
}

/** Open-round filter keeps rows that can be pitched. Already-funded keeps rows that hold money. `both` belongs in each. */
export function accountMatchesFundingStage(row, stage) {
  if (!stage || stage === 'all') return true;
  const basis = row?.funding_basis;
  if (stage === 'open_round') return basis === 'open_round' || basis === 'both';
  if (stage === 'prior_award') return basis === 'prior_award' || basis === 'both';
  return true;
}

export function filterFundingAccounts(accounts, { query = '', country = 'all', stage = 'all' } = {}) {
  const q = String(query || '').trim().toLowerCase();
  const selected = country && country !== 'all' ? String(country) : '';
  return (accounts || []).filter((row) => {
    if (selected && String(row?.country || '') !== selected) return false;
    if (q && !String(row?.account_name || '').toLowerCase().includes(q)) return false;
    if (!accountMatchesFundingStage(row, stage)) return false;
    return true;
  });
}

function compareText(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });
}

function fundingSortValue(row, key) {
  if (key === 'fit') {
    const n = Number(row?.market_score);
    return Number.isFinite(n) ? n : null;
  }
  if (key === 'in_market') return row?.in_sam ? 1 : 0;
  const field = {
    account: 'account_name',
    eligibility: 'total_funding_eligibility',
    country: 'country',
    standing: 'standing_label',
    stage: 'funding_basis',
  }[key];
  const value = row?.[field];
  if (value == null || String(value).trim() === '') return null;
  return String(value);
}

/** Text columns sort A–Z. Fit sorts high to low. Empty values stay at the bottom. */
export function sortFundingAccounts(accounts, key, direction = 'asc') {
  const dir = direction === 'desc' ? -1 : 1;
  const numeric = key === 'fit' || key === 'in_market';
  return [...(accounts || [])].sort((a, b) => {
    const av = fundingSortValue(a, key);
    const bv = fundingSortValue(b, key);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (numeric) return (av - bv) * dir;
    return compareText(av, bv) * dir;
  });
}
